import { AnyAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { sitesClient } from '../../../api/client';
import { QueryDependencies, QuerySite, SiteRequestReadyForPickup, SiteRequestSite, SiteRequestSiteGeofence, SiteRequestSiteJob } from '../../../api/schema';
import { getPagination, keysToId } from '../../../helpers/sliceHelper';
import { RootState } from '../../store';
import {
  AlternateSitesType,
  AlternateSitesTypeMapped,
  QuerySiteMapped,
  SiteEquipmentSummaryType,
  SiteKPIType,
  SitesListQuery,
  SitesState,
  SitesType,
} from './sitesInterface';

export const sitesListAdapter = createEntityAdapter<SitesType>({
  selectId: (list) => list.id || '',
});

export const siteDetailsAdapter = createEntityAdapter<QuerySite>({
  selectId: (site) => site.id || '',
});

export const siteDependenciesAdapter = createEntityAdapter<QueryDependencies>({
  selectId: (dependency) => dependency.id || '',
});

export const listAlternateSitesAdapter = createEntityAdapter<AlternateSitesType>({
  selectId: (list) => list.id || '',
});

export const siteKPIAdapter = createEntityAdapter<SiteKPIType>({
  selectId: (siteKPI) => siteKPI.id || '',
});
export const siteEquipmentSummaryAdapter = createEntityAdapter<SiteEquipmentSummaryType>({
  selectId: (entry) => entry.id || '',
});

export const listSite = createAsyncThunk('sites/listSite', async (query: SitesListQuery) => {
  const argumentsKeys = ['organizationId', 'siteOwner', 'search', 'customerUuid', 'active', 'sortBy', 'offset', 'limit', 'options'];
  return sitesClient.listSite.apply(
    this,
    argumentsKeys.map((key) => query[key]) as [
      organizationId?: string,
      siteOwner?: string,
      search?: string,
      customerUuid?: string,
      active?: string,
      sortBy?: string,
      offset?: number,
      limit?: number,
      options?: Record<string, unknown>,
    ],
  );
});

export const getSiteByUuid = createAsyncThunk('sites/getSiteByUuid', async (siteId: string) => sitesClient.getSiteById(siteId));
export const getSiteKpis = createAsyncThunk('sites/getSiteKpis', async (siteId: string) => sitesClient.getSiteKpis(siteId));
export const getSiteEquipmentSummary = createAsyncThunk('sites/getSiteEquipmentSummary', async (siteId: string) => sitesClient.getSiteEquipmentSummary(siteId));
export const updateSite = createAsyncThunk('sites/updateSite', async (payload: SiteRequestSite) => sitesClient.updateSite(payload));
// Rejects with the API's status, error code and messages so the form can put a
// conflict on the right field (a taken job number, a duplicate site name).
export const createSite = createAsyncThunk('sites/createSite', async (payload: SiteRequestSite, { rejectWithValue }) => {
  try {
    return await sitesClient.createSite(payload);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error?.response) {
      return rejectWithValue({ status: error.response.status, code: error.response.data?.code, messages: error.response.data?.messages });
    }
    throw error;
  }
});
export const getSiteDependencies = createAsyncThunk('sites/getSiteDependencies', async (siteId: string) => sitesClient.getSiteDependencies(siteId));
export const getListAlternateSites = createAsyncThunk('sites/listAlternateSites', async (siteId: string) => sitesClient.listAlternateSites(siteId));
export const mergeSite = createAsyncThunk('sites/mergeSite', async ({ siteId, targetSiteId }: { siteId: string; targetSiteId: string }) =>
  sitesClient.mergeSite(siteId, targetSiteId),
);
export const setSiteGeofence = createAsyncThunk('sites/setSiteGeofence', async ({ siteId, geofence }: { siteId: string; geofence: SiteRequestSiteGeofence }) =>
  sitesClient.setSiteGeofence(siteId, geofence),
);

export const setSiteReadyForPickup = createAsyncThunk(
  'sites/setSiteReadyForPickup',
  async ({ siteId, body }: { siteId: string; body: SiteRequestReadyForPickup }) => sitesClient.setSiteReadyForPickup(siteId, body),
);

// Create, update or clear the site's client job number. Dedicated endpoint, not
// PUT /sites: the job row and the site link are written together, and the
// full-replace PUT would require round-tripping every other site field.
export const setSiteJob = createAsyncThunk('sites/setSiteJob', async ({ siteId, body }: { siteId: string; body: SiteRequestSiteJob }) =>
  sitesClient.setSiteJob(siteId, body),
);

export const sitesSlice = createSlice({
  name: 'sites',
  initialState: {
    sites: sitesListAdapter.getInitialState(),
    siteDetails: siteDetailsAdapter.getInitialState(),
    siteDependencies: siteDependenciesAdapter.getInitialState(),
    listAlternateSites: listAlternateSitesAdapter.getInitialState(),
    siteKPI: siteKPIAdapter.getInitialState(),
    siteEquipmentSummary: siteEquipmentSummaryAdapter.getInitialState(),
  } as SitesState,
  reducers: {
    removeSite: (state, action: PayloadAction<string>) => {
      siteDetailsAdapter.removeOne(state.siteDetails, action.payload);
      sitesListAdapter.removeOne(state.sites, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listSite.pending, (state: SitesState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), listSite: true };
    });
    builder.addCase(listSite.fulfilled, (state: SitesState, action: AnyAction) => {
      state.fetching = { ...state.fetching, listSite: false };
      if (action.payload.data.result) {
        sitesListAdapter.upsertOne(state.sites, { id: keysToId(action.meta.arg), ...action.payload.data });
      }
    });
    builder.addCase(listSite.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, listSite: false };
    });

    builder.addCase(getSiteByUuid.pending, (state: SitesState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getSiteByUuid: true };
    });
    builder.addCase(getSiteByUuid.fulfilled, (state: SitesState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getSiteByUuid: false };
      if (action.payload.data.result) {
        siteDetailsAdapter.upsertOne(state.siteDetails, action.payload.data.result);
      }
    });
    builder.addCase(getSiteByUuid.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getSiteByUuid: false };
    });

    builder.addCase(updateSite.pending, (state: SitesState) => {
      state.error = undefined;
      state.updating = true;
    });
    builder.addCase(updateSite.fulfilled, (state: SitesState, action: AnyAction) => {
      state.updating = false;
      if (action.payload.data.result) {
        siteDetailsAdapter.updateOne(state.siteDetails, { id: action.payload.data.result.id, changes: action.payload.data.result });
      }
    });
    builder.addCase(updateSite.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.updating = false;
    });
    builder.addCase(setSiteReadyForPickup.pending, (state: SitesState) => {
      state.error = undefined;
      state.updating = true;
    });
    builder.addCase(setSiteReadyForPickup.fulfilled, (state: SitesState, action: AnyAction) => {
      state.updating = false;
      if (action.payload.data.result) {
        siteDetailsAdapter.updateOne(state.siteDetails, { id: action.payload.data.result.id, changes: action.payload.data.result });
      }
    });
    builder.addCase(setSiteReadyForPickup.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.updating = false;
    });
    builder.addCase(setSiteJob.pending, (state: SitesState) => {
      state.error = undefined;
      state.updating = true;
    });
    builder.addCase(setSiteJob.fulfilled, (state: SitesState, action: AnyAction) => {
      state.updating = false;
      if (action.payload.data.result) {
        siteDetailsAdapter.updateOne(state.siteDetails, { id: action.payload.data.result.id, changes: action.payload.data.result });
      }
    });
    builder.addCase(setSiteJob.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.updating = false;
    });

    builder.addCase(getSiteDependencies.pending, (state: SitesState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getSiteDependencies: true };
    });
    builder.addCase(getSiteDependencies.fulfilled, (state: SitesState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getSiteDependencies: false };
      siteDependenciesAdapter.upsertOne(state.siteDependencies, action.payload.data.result);
    });
    builder.addCase(getSiteDependencies.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getSiteDependencies: false };
    });
    builder.addCase(getListAlternateSites.pending, (state: SitesState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getListAlternateSites: true };
    });
    builder.addCase(getListAlternateSites.fulfilled, (state: SitesState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getListAlternateSites: false };
      listAlternateSitesAdapter.upsertOne(state.listAlternateSites, { id: action.meta.arg, result: action.payload.data.result });
    });
    builder.addCase(getListAlternateSites.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getListAlternateSites: false };
    });
    builder.addCase(getSiteKpis.pending, (state: SitesState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getSiteKpis: true };
    });
    builder.addCase(getSiteKpis.fulfilled, (state: SitesState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getSiteKpis: false };
      if (action.payload.data.result) {
        siteKPIAdapter.upsertOne(state.siteKPI, { id: action.meta.arg, kpi: action.payload.data.result });
      }
    });
    builder.addCase(getSiteKpis.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getSiteKpis: false };
    });
    builder.addCase(getSiteEquipmentSummary.pending, (state: SitesState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), getSiteEquipmentSummary: true };
    });
    builder.addCase(getSiteEquipmentSummary.fulfilled, (state: SitesState, action: AnyAction) => {
      state.fetching = { ...state.fetching, getSiteEquipmentSummary: false };
      siteEquipmentSummaryAdapter.upsertOne(state.siteEquipmentSummary, { id: action.meta.arg, rows: action.payload.data.result ?? [] });
    });
    builder.addCase(getSiteEquipmentSummary.rejected, (state: SitesState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, getSiteEquipmentSummary: false };
    });
  },
});

export const { selectById: selectSitesById } = sitesListAdapter.getSelectors((state: RootState) => state.sites.sites);
export const { selectById: selectSiteDetailsById } = siteDetailsAdapter.getSelectors((state: RootState) => state.sites.siteDetails);
export const { selectById: selectSiteDependenciesById } = siteDependenciesAdapter.getSelectors((state: RootState) => state.sites.siteDependencies);
export const { selectById: selectAlternateSitesById } = listAlternateSitesAdapter.getSelectors((state: RootState) => state.sites.listAlternateSites);
export const { selectById: selectSiteKPIById } = siteKPIAdapter.getSelectors((state: RootState) => state.sites.siteKPI);
export const { selectById: selectSiteEquipmentSummaryById } = siteEquipmentSummaryAdapter.getSelectors((state: RootState) => state.sites.siteEquipmentSummary);

const selectSiteKPIByIdSelector = createSelector(
  [selectSiteKPIById],
  (vehicle) =>
    vehicle?.kpi.reduce(
      (partKPIs, kpi) => (kpi.metric ? { ...partKPIs, [kpi.metric]: kpi.int_64_value } : partKPIs),
      {} as { [key: string]: number | undefined },
    ) ?? {},
);
export const getSiteKPIById =
  (siteID = '') =>
  (state: RootState): { [key: string]: number | undefined } =>
    selectSiteKPIByIdSelector(state, siteID);
export const getSiteEquipmentSummaryById =
  (siteID = '') =>
  (state: RootState): SiteEquipmentSummaryType | undefined =>
    selectSiteEquipmentSummaryById(state, siteID);
export const getSiteEquipmentSummaryFetching = (state: RootState): boolean => state.sites.fetching?.getSiteEquipmentSummary !== false;

const selectSiteDetailsByIdMappedSelector = createSelector([selectSiteDetailsById], (details) => {
  const getVehicleLink = (vehicle: string): [string | undefined, string] => {
    const linkTitle = vehicle.split(':');

    return linkTitle[1] ? [linkTitle[0], linkTitle[1]] : [undefined, vehicle];
  };

  return details
    ? {
        ...details,
        vehicleLinkMapped: details?.vehicles
          ?.split(', ')
          .reduce((partLinks, vehicle) => [...partLinks, getVehicleLink(vehicle)], [] as [string | undefined, string][]),
      }
    : details;
});

export const getSiteDetailsById =
  (siteId = '') =>
  (state: RootState): QuerySiteMapped | undefined =>
    selectSiteDetailsByIdMappedSelector(state, siteId);

const getSitesByIdSelector = createSelector(
  [selectSitesById, (state: RootState, keys: string, organizationId: string | undefined) => organizationId],
  (sites, organizationId) => ({
    pagination: getPagination(sites),
    totals: sites?.totals,
    result: sites?.result
      ?.filter((site) => (organizationId ? site.organization_id === organizationId : true))
      .map((site) => ({
        ...site,
        addressMapped: site.address1,
        customerMapped: site.customer_id && site.customer ? site.customer.name : 'Unassigned',
        activeMapped: site.active ? 'Active' : 'Disabled',
      })),
  }),
);

export const getSitesById =
  (keys: SitesListQuery, organizationId?: string) =>
  (state: RootState): SitesType =>
    getSitesByIdSelector(state, keysToId(keys), organizationId);

const getSitesOptionsByIdSelector = createSelector(
  [selectSitesById],
  (sites) =>
    sites?.result
      ?.filter((site) => site.name && site.id)
      .map((site) => ({
        label: site.name || '',
        id: site.id || '',
      })) || ([] as { label: string; id: string }[]),
);

export const getSitesOptionsById =
  (keys: SitesListQuery) =>
  (state: RootState): { label: string; id: string }[] =>
    getSitesOptionsByIdSelector(state, keysToId(keys));

export const getSiteDependenciesById =
  (siteId = '') =>
  (state: RootState): QueryDependencies | undefined =>
    selectSiteDependenciesById(state, siteId);

const selectAlternateSitesByIdSelector = createSelector([selectAlternateSitesById], (sites) =>
  sites
    ? {
        ...sites,
        result: sites.result?.map((site) => ({ ...site, distanceMapped: site.distance ? (site.distance * 0.00062137).toFixed(2) : site.distance })),
      }
    : sites,
);
export const getAlternateSites =
  (siteId = '') =>
  (state: RootState): AlternateSitesTypeMapped | undefined =>
    selectAlternateSitesByIdSelector(state, siteId);

export const getSitesByIdFetching = (state: RootState): boolean => state.sites.fetching?.listSite !== false;
export const getSiteByUuidFetching = (state: RootState): boolean => state.sites.fetching?.getSiteByUuid !== false;
export const getSiteDependenciesFetching = (state: RootState): boolean => state.sites.fetching?.getSiteDependencies !== false;
export const getAlternateSitesFetching = (state: RootState): boolean => state.sites.fetching?.getListAlternateSites !== false;
export const siteKpisFetching = (state: RootState): boolean => state.sites.fetching?.getSiteKpis !== false;

export const { removeSite } = sitesSlice.actions;

export default sitesSlice.reducer;
